import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Modal,
  Group,
  Button,
  Avatar,
  Checkbox,
  Stack,
  Text,
  ScrollArea,
  Badge,
  Divider,
  Box,
  Paper,
} from '@mantine/core';
import { IconUsers, IconBuilding, IconChevronRight, IconUser, IconSearch } from '@tabler/icons-react';
import { useAdminStore } from '@/store/admin';
import { useTranslation } from 'react-i18next';
import { useGPTStore } from '@/store';

// 部门类型
interface Department {
  id: string;
  name: string;
  parentId?: string | number;
  children?: Department[];
}

// 用户类型
interface User {
  id: string;
  uuid?: string;
  name: string;
  email: string;
  avatar?: string;
  authorityCodes: string[];
  departmentIds?: string[];
  roles: { id: number; code: string; name: string }[];
}

interface LocalMemberSelectorProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selected: User[]) => void | Promise<void>;
  defaultSelectedUserIds?: string[];
  isUserSelectable?: (user: User) => boolean;
  maxSelect?: number; // 最大选择数量，1 表示单选模式
  // 嵌入模式：跳过 Modal 外壳，直接渲染选择器内容（用于在外层 Modal 的 Tab 中复用）
  embedded?: boolean;
}

// 角色禁用判断
const isViewerOnly = (user: User) =>
  Array.isArray(user.roles) && user.roles.length > 0 && user.roles.every((r) => r.code === 'ROLE_VIEWER');

// 判断是否已达到最大选择数量
const isMaxSelected = (selectedCount: number, maxSelect?: number): boolean => {
  return maxSelect !== undefined && selectedCount >= maxSelect;
};

// 角色标签颜色
const roleColor = (code: string) => {
  switch (code) {
    case 'ROLE_COMPANY_OWNER':
      return 'red';
    case 'ROLE_ADMIN':
      return 'orange';
    case 'ROLE_EDITOR':
      return 'violet';
    case 'ROLE_VIEWER':
      return 'green';
    default:
      return 'gray';
  }
};

// 递归构建部门树
const buildTree = (departments: Department[], parentId: string | number = '-1'): Department[] => {
  return departments
    .filter((d: Department) => String(d.parentId ?? '-1') === String(parentId))
    .map((d: Department) => ({ ...d, children: buildTree(departments, d.id) }));
};

const LocalMemberSelector: React.FC<LocalMemberSelectorProps> = ({
  open,
  onClose,
  onConfirm,
  defaultSelectedUserIds = [],
  isUserSelectable,
  maxSelect,
  embedded = false,
}) => {
  const { t } = useTranslation();
  const { departmentTree = [], isDataReady, noneDepartmentUsers = [], userOgsId } = useAdminStore();
  const { userInfo } = useGPTStore();

  // 构造带"未分配成员"节点的新部门树
  // 注意：不依赖 t 函数，避免无限循环
  const enhancedDepartmentTree = useMemo(() => {
    if (!Array.isArray(noneDepartmentUsers) || noneDepartmentUsers.length === 0) return departmentTree;
    return [
      {
        id: 'none',
        name: t('adminConsole.organization.noneDepartmentUsers'),
        members: noneDepartmentUsers,
        parentId: '-1',
        children: [],
        isVirtual: true,
      },
      ...departmentTree,
    ];
  }, [departmentTree, noneDepartmentUsers]);

  // 当前选中的部门节点，默认第一个一级部门（优先选择"未分配成员"节点）
  const [currentDept, setCurrentDept] = useState<any>(null);

  useEffect(() => {
    if (enhancedDepartmentTree.length > 0 && !currentDept) {
      setCurrentDept(enhancedDepartmentTree[0]);
    }
  }, [enhancedDepartmentTree]);

  // 内部状态管理选中用户ID
  const [internalSelectedUserIds, setInternalSelectedUserIds] = useState<string[]>(defaultSelectedUserIds);

  // 当defaultSelectedUserIds更新时，同步更新内部状态
  // 使用 useRef 跟踪上一次的值，避免无限循环
  const prevDefaultSelectedUserIds = useRef<string[] | undefined>();
  useEffect(() => {
    // 只有当 defaultSelectedUserIds 真正变化时才更新（通过比较内容或长度）
    const prev = prevDefaultSelectedUserIds.current;
    const current = defaultSelectedUserIds || [];
    const isChanged = !prev || prev.length !== current.length || prev.some((id, i) => id !== current[i]);

    if (isChanged) {
      setInternalSelectedUserIds(current);
      prevDefaultSelectedUserIds.current = current;
    }
  }, [defaultSelectedUserIds]);

  // 使用内部状态作为当前选中的用户ID
  const selectedUserIds = internalSelectedUserIds;

  // 跟踪是否有任何选择状态变化（用于判断是否需要调用接口）
  const [hasSelectionChanged, setHasSelectionChanged] = useState(false);

  // 递归获取所有用户
  const getAllUsers = (tree: any[]): any[] => {
    let users: any[] = [];
    tree.forEach((dept) => {
      if (dept.members) {
        users = users.concat(dept.members);
      }
      if (dept.children && dept.children.length > 0) {
        users = users.concat(getAllUsers(dept.children));
      }
    });
    return users;
  };

  // 所有用户列表
  const allUsers = useMemo(() => {
    // 过滤掉当前用户（优先用OGS userId匹配，email可能带OAuth前缀）
    const users = getAllUsers(enhancedDepartmentTree).filter((user) =>
      userOgsId ? String(user.id) !== userOgsId : user.email !== userInfo?.email
    );
    console.log('[LocalMemberSelector] allUsers:', users);
    console.log('[LocalMemberSelector] noneDepartmentUsers:', noneDepartmentUsers);
    console.log('[LocalMemberSelector] enhancedDepartmentTree:', enhancedDepartmentTree);
    return users;
  }, [enhancedDepartmentTree, userInfo?.email, userOgsId]);

  // 递归渲染部门树
  const renderDeptTree = (tree: any[], level: number = 0) => (
    <Stack gap={1}>
      {tree.map((dept, index) => {
        const hasChildren = dept.children && dept.children.length > 0;
        const isSelected = currentDept?.id === dept.id;
        const memberCount = dept.members?.length || 0;
        const isLast = index === tree.length - 1;
        const isVirtual = dept.isVirtual; // 是否为虚拟节点（未分配成员）

        return (
          <div key={dept.id} style={{ position: 'relative' }}>
            {/* 垂直连接线 */}
            {level > 0 && !isLast && (
              <div
                style={{
                  position: 'absolute',
                  left: 8 + (level - 1) * 8,
                  top: 24,
                  width: 1,
                  height: 'calc(100% - 8px)',
                  backgroundColor: 'var(--mantine-color-gray-2)',
                  opacity: 0.7,
                }}
              />
            )}

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 8,
                cursor: 'pointer',
                borderRadius: 6,
                backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : 'transparent',
                border: isSelected ? '1px solid var(--mantine-color-blue-3)' : '1px solid transparent',
                transition: 'all 0.2s ease',
                paddingLeft: 16 + level * 8,
                position: 'relative',
                marginBottom: hasChildren ? 4 : 0,
                minHeight: 36,
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
              onClick={() => setCurrentDept(dept)}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-0)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {/* 水平连接线 */}
              {level > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    left: 8 + (level - 1) * 20,
                    top: '50%',
                    width: 12,
                    height: 1,
                    backgroundColor: 'var(--mantine-color-gray-3)',
                    transform: 'translateY(-50%)',
                  }}
                />
              )}
              <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 8 }}>
                <IconBuilding
                  size={level === 0 ? 18 : 16}
                  color={
                    isSelected
                      ? 'var(--mantine-color-blue-6)'
                      : level === 0
                      ? 'var(--mantine-color-gray-7)'
                      : 'var(--mantine-color-gray-6)'
                  }
                />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: isSelected ? 600 : level === 0 ? 600 : level === 1 ? 500 : 400,
                    color: isSelected
                      ? 'var(--mantine-color-blue-7)'
                      : level === 0
                      ? 'var(--mantine-color-gray-8)'
                      : level === 1
                      ? 'var(--mantine-color-gray-7)'
                      : 'var(--mantine-color-gray-6)',
                    fontSize: level === 0 ? '15px' : level === 1 ? '14px' : '13px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '20px',
                  }}
                  title={dept.name}
                >
                  {dept.name}
                </span>
                {memberCount > 0 && (
                  <Badge
                    size="xs"
                    variant="light"
                    color={
                      (dept.members || []).some((u: any) => selectedUserIds.includes(String(u.id))) ? 'blue' : 'gray'
                    }
                    style={{
                      fontSize: level === 0 ? '11px' : '10px',
                      flexShrink: 0,
                      marginLeft: 2,
                    }}
                  >
                    {memberCount}
                  </Badge>
                )}
              </div>
              {hasChildren && (
                <IconChevronRight
                  size={level === 0 ? 16 : 14}
                  color={isSelected ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-gray-5)'}
                  style={{ flexShrink: 0, marginLeft: 4 }}
                />
              )}
            </div>

            {/* 子部门容器 */}
            {hasChildren && (
              <div
                style={{
                  marginLeft: 12,
                  paddingLeft: 8,
                  borderLeft: level === 0 ? '1px solid var(--mantine-color-gray-2)' : 'none',
                }}
              >
                {renderDeptTree(dept.children, level + 1)}
              </div>
            )}
          </div>
        );
      })}
    </Stack>
  );

  // 当前部门成员和子部门
  const currentUsers = (currentDept?.members || []).filter((user: any) => {
    // 如果当前用户数据不存在，不过滤，全部使用。
    if (!userInfo) return true;

    // 过滤掉当前用户（优先用OGS userId匹配，email可能带OAuth前缀）
    return userOgsId ? String(user.id) !== userOgsId : user.email !== userInfo.email;
  });

  // 已选用户详情（从所有用户中获取），根据邮箱去重
  const selectedUsers = useMemo(() => {
    // 确保 ID 匹配时考虑类型转换
    const filtered = allUsers.filter((u: any) => {
      const userId = String(u.id);
      const isSelected = selectedUserIds.some((selectedId) => String(selectedId) === userId);
      return isSelected;
    });
    // 根据邮箱去重
    const uniqueByEmail: Record<string, any> = {};
    filtered.forEach((user) => {
      if (user.email && !uniqueByEmail[user.email]) {
        uniqueByEmail[user.email] = user;
      }
    });
    return Object.values(uniqueByEmail);
  }, [allUsers, selectedUserIds]);

  // 用户多选逻辑
  const handleUserCheck = (user: User, checked: boolean) => {
    const userId = String(user.id);
    if (checked) {
      // 单选模式：直接替换为当前选中的用户
      if (maxSelect === 1) {
        setInternalSelectedUserIds([userId]);
      } else {
        // 多选模式：检查是否达到最大选择数量
        if (isMaxSelected(selectedUserIds.length, maxSelect)) {
          return; // 已达到最大数量，不再添加
        }
        setInternalSelectedUserIds((prev) => [...prev, userId]);
      }
    } else {
      setInternalSelectedUserIds((prev) => prev.filter((id) => String(id) !== userId));
    }
    setHasSelectionChanged(true);
  };

  // 已选成员移除
  const handleRemoveSelected = (userId: string) => {
    setInternalSelectedUserIds((prev) => prev.filter((id) => String(id) !== String(userId)));
    setHasSelectionChanged(true);
  };

  // 全选/取消全选当前部门
  const handleSelectAll = () => {
    const currentUserIds = currentUsers.map((u: any) => String(u.id));
    const allSelected = currentUserIds.every((id: string) => selectedUserIds.includes(id));

    if (allSelected) {
      // 取消全选
      setInternalSelectedUserIds((prev) => prev.filter((id) => !currentUserIds.includes(id)));
    } else {
      // 全选逻辑：根据isUserSelectable参数决定是否包含查看者角色用户
      let selectableUsers = currentUsers;

      // 如果没有传入isUserSelectable参数，使用默认逻辑（排除查看者角色用户）
      if (!isUserSelectable) {
        selectableUsers = currentUsers.filter((u: any) => !isViewerOnly(u));
      } else {
        // 如果传入了isUserSelectable参数，使用该函数过滤用户
        selectableUsers = currentUsers.filter((u: any) => isUserSelectable(u));
      }

      const selectableIds = selectableUsers.map((u: any) => String(u.id));
      setInternalSelectedUserIds((prev) => [...new Set([...prev, ...selectableIds])]);
    }
    setHasSelectionChanged(true);
  };

  const currentUserIds = currentUsers.map((u: any) => String(u.id));
  const allSelected = currentUserIds.every((id: string) => selectedUserIds.includes(id));
  const someSelected = currentUserIds.some((id: string) => selectedUserIds.includes(id)) && !allSelected;

  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // 确认选择处理函数
  const handleConfirmSelection = async () => {
    // 如果有选择状态变化，则调用接口
    if (hasSelectionChanged) {
      setConfirmLoading(true);
      try {
        await onConfirm(selectedUsers);
      } finally {
        setConfirmLoading(false);
      }
    } else {
      // 如果没有选择状态变化，直接关闭弹窗
      onClose();
    }
  };

  // 搜索过滤用户
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return allUsers.filter((u) => !selectedUserIds.includes(String(u.id)));
    const kw = search.trim().toLowerCase();
    return allUsers.filter(
      (u) =>
        !selectedUserIds.includes(String(u.id)) &&
        (u.name?.toLowerCase().includes(kw) || u.email?.toLowerCase().includes(kw))
    );
  }, [allUsers, search, selectedUserIds]);

  const content = (
    <>
      {/* 加载中处理 - 始终渲染，但根据条件显示不同内容 */}
      {!isDataReady ? (
        <Box style={{ minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text c="gray.5">{t('adminConsole.customUserMultiSelect.loadingData')}</Text>
        </Box>
      ) : (
        <>
          {/* 搜索输入框+下拉 */}
          <div style={{ position: 'relative', marginBottom: 18 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1.5px solid #E5E7EB',
                borderRadius: 12,
                background: '#fff',
                padding: '0 12px',
                height: 44,
              }}
            >
              <IconSearch size={20} color="#B0B8C4" />
              <input
                ref={inputRef}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                placeholder={t('localMemberSelector.searchPlaceholder')}
                style={{
                  border: 'none',
                  outline: 'none',
                  flex: 1,
                  fontSize: 16,
                  background: 'transparent',
                  marginLeft: 8,
                }}
              />
              <Button
                size="xs"
                variant="subtle"
                color="violet"
                style={{ fontWeight: 600, fontSize: 18, padding: '0 8px' }}
                onClick={() => {
                  setShowDropdown(true);
                  inputRef.current?.focus();
                }}
              >
                ＋
              </Button>
            </div>
            {/* 下拉列表 */}
            {showDropdown && filteredUsers.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: 48,
                  left: 0,
                  right: 0,
                  background: '#fff',
                  borderRadius: 14,
                  boxShadow: '0 8px 32px 0 rgba(30,41,59,0.10)',
                  zIndex: 10,
                  padding: '8px 0',
                  maxHeight: 320,
                  overflowY: 'auto',
                  border: '1.5px solid #E5E7EB',
                }}
              >
                <div style={{ padding: '4px 18px 6px 18px', fontSize: 13, color: '#8B95A1', fontWeight: 600 }}>
                  {t('adminConsole.customUserMultiSelect.people')}
                </div>
                {filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 18px',
                      cursor: 'pointer',
                      background: '#fff',
                      transition: 'background 0.2s',
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      // 单选模式：直接替换
                      if (maxSelect === 1) {
                        setInternalSelectedUserIds([String(user.id)]);
                      } else {
                        // 多选模式：检查是否达到最大数量
                        if (isMaxSelected(selectedUserIds.length, maxSelect)) {
                          return;
                        }
                        setInternalSelectedUserIds((prev) => [...prev, String(user.id)]);
                      }
                      setHasSelectionChanged(true);
                      setSearch('');
                      setShowDropdown(false);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#F4F6FA')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                  >
                    <IconUser size={20} color="#B0B8C4" style={{ marginRight: 10 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 500,
                          fontSize: 15,
                          color: '#222',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {user.name || user.email}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: '#8B95A1',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {user.email}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row', gap: 24 }}>
            {/* 部门树 */}
            <Paper
              shadow="xs"
              p={16}
              style={{
                minWidth: 300,
                height: '100%',
                flex: '0 0 300px',
                overflowY: 'auto',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Text fw={600} mb={12} c="gray.7">
                {t('adminConsole.customUserMultiSelect.departmentStructure')}
              </Text>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{renderDeptTree(enhancedDepartmentTree)}</div>
            </Paper>
            {/* 成员展示区域 */}
            <Box style={{ flex: 1, minWidth: 280, height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* 当前部门信息 */}
              <Group justify="space-between" mb={16}>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 8 }}>
                  <IconBuilding size={18} color="var(--mantine-color-blue-6)" />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontWeight: 600,
                      fontSize: '18px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={currentDept?.name || t('adminConsole.customUserMultiSelect.selectDepartment')}
                  >
                    {currentDept?.name || t('adminConsole.customUserMultiSelect.selectDepartment')}
                  </span>
                  {currentUsers.length > 0 && (
                    <Badge size="sm" variant="light" color="blue" style={{ flexShrink: 0 }}>
                      {currentUsers.length} {t('localMemberSelector.people')}
                    </Badge>
                  )}
                </div>
                {currentUsers.length > 0 && (
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                    label={t('adminConsole.customUserMultiSelect.selectAll')}
                    size="sm"
                  />
                )}
              </Group>
              {/* 成员列表 */}
              <Paper
                shadow="xs"
                p={0}
                style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}
              >
                <ScrollArea style={{ flex: 1 }}>
                  <Stack gap={0}>
                    {currentUsers.length > 0 ? (
                      currentUsers.map((user: any) => {
                        const isSelected = selectedUserIds.some((selectedId) => String(selectedId) === String(user.id));
                        const isDisabled = isUserSelectable ? !isUserSelectable(user) : isViewerOnly(user);
                        return (
                          <Group
                            key={user.id}
                            justify="space-between"
                            p={12}
                            style={{
                              borderBottom: '1px solid var(--mantine-color-gray-2)',
                              backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : 'transparent',
                              opacity: isDisabled ? 0.6 : 1,
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected && !isDisabled) {
                                e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-0)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected && !isDisabled) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <Group gap={12}>
                              <Avatar src={user.avatar} radius="xl" size="md" />
                              <Stack gap={2}>
                                <Group gap={6} align="center">
                                  <Text
                                    size="sm"
                                    fw={500}
                                    style={{
                                      maxWidth: 120,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={user.name}
                                  >
                                    {user.name}
                                  </Text>
                                  {Array.isArray(user.roles) && user.roles.length > 0 ? (
                                    user.roles.map((role: any) => (
                                      <Badge
                                        key={role.code || role.id}
                                        color={roleColor(role.code)}
                                        size="xs"
                                        variant="light"
                                        style={{
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                        title={
                                          role.code
                                            ? t(`adminConsole.employees.roles.${role.code}`)
                                            : role.name ||
                                              role.code ||
                                              t('adminConsole.customUserMultiSelect.unknownRole')
                                        }
                                      >
                                        {role.code
                                          ? t(`adminConsole.employees.roles.${role.code}`)
                                          : role.name ||
                                            role.code ||
                                            t('adminConsole.customUserMultiSelect.unknownRole')}
                                      </Badge>
                                    ))
                                  ) : (
                                    <Badge color="gray" size="xs" variant="light">
                                      {t('adminConsole.customUserMultiSelect.noRole')}
                                    </Badge>
                                  )}
                                </Group>
                                <Text
                                  size="xs"
                                  c="gray.5"
                                  style={{
                                    maxWidth: 180,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={user.email}
                                >
                                  {user.email}
                                </Text>
                              </Stack>
                            </Group>
                            <Checkbox
                              checked={isSelected}
                              disabled={isDisabled}
                              onChange={(e) => handleUserCheck(user, e.currentTarget.checked)}
                              size="md"
                            />
                          </Group>
                        );
                      })
                    ) : (
                      <Box
                        p={40}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                        }}
                      >
                        <IconUsers size={48} color="var(--mantine-color-gray-4)" style={{ margin: '0 auto' }} />
                        <Text c="gray.5" ta="center" py={40}>
                          {t('adminConsole.customUserMultiSelect.noSelectedMembers')}
                        </Text>
                      </Box>
                    )}
                  </Stack>
                </ScrollArea>
              </Paper>
            </Box>
          </div>
          {/* 已选成员展示 - 独立底部模块 */}
          <Paper
            shadow="xs"
            p={16}
            style={{
              borderTop: '2px solid var(--mantine-color-blue-2)',
              marginTop: 20,
              background: 'var(--mantine-color-gray-0)',
              maxHeight: 180,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Group justify="space-between" mb={12} style={{ flexShrink: 0 }}>
              <Group gap={8}>
                <IconUsers size={16} color="var(--mantine-color-violet-6)" />
                <Text fw={600} c="gray.7">
                  {t('localMemberSelector.selectedMembers')}
                </Text>
                <Badge size="sm" color="violet" variant="light">
                  {selectedUsers.length} {t('localMemberSelector.people')}
                </Badge>
              </Group>
              {selectedUsers.length > 0 && (
                <Text size="xs" c="gray.5">
                  {t('localMemberSelector.removeTip')}
                </Text>
              )}
            </Group>
            <ScrollArea style={{ flex: 1, minHeight: 0 }}>
              <Group gap={10} wrap="wrap" mih={36}>
                {selectedUsers.length > 0 ? (
                  selectedUsers.map((user: any) => (
                    <Badge
                      key={user.id}
                      color="violet"
                      variant="light"
                      size="md"
                      style={{
                        background: 'var(--mantine-color-violet-0)',
                        fontSize: '15px',
                        fontWeight: 500,
                        padding: '6px 10px',
                        borderRadius: 16,
                        maxWidth: 220,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      rightSection={
                        <span
                          style={{
                            cursor: 'pointer',
                            marginLeft: 6,
                            fontSize: '18px',
                            color: 'var(--mantine-color-violet-7)',
                          }}
                          onClick={() => handleRemoveSelected(user.id)}
                        >
                          ×
                        </span>
                      }
                    >
                      <Group gap={6} align="center" style={{ flex: 1, minWidth: 0 }}>
                        <Avatar src={user.avatar} radius="xl" size="sm" />
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={user.name}
                        >
                          {user.name}
                        </span>
                      </Group>
                    </Badge>
                  ))
                ) : (
                  <Text size="md" c="gray.5" style={{ fontStyle: 'italic' }}>
                    {t('localMemberSelector.noSelectedMembers')}
                  </Text>
                )}
              </Group>
            </ScrollArea>
          </Paper>
          {/* 操作按钮 */}
          <Group justify="flex-end" pt={16} style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
            <Button variant="outline" color="gray" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button loading={confirmLoading} onClick={handleConfirmSelection}>
              {t('localMemberSelector.confirmSelection')} ({selectedUsers.length})
            </Button>
          </Group>
        </>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Modal
      opened={open}
      onClose={onClose}
      size="xl"
      title={
        <Group gap={8}>
          <IconUsers size={20} />
          <Text fw={600}>{t('localMemberSelector.title')}</Text>
        </Group>
      }
      centered
      styles={{
        title: {
          fontSize: '18px',
          fontWeight: 600,
        },
        body: {
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          height: 600,
          minHeight: 400,
          boxSizing: 'border-box',
        },
      }}
    >
      {content}
    </Modal>
  );
};

export default LocalMemberSelector;
